import './GeneratedAssets.scss'
import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { FC, Fragment, useMemo } from 'react'
import { StepperProps } from '../../../utils/stepper-types'
import { ExportSvg, InfoSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import useForm from '../../../hooks/useForm'
import useScenarioResponse from '../../../hooks/useScenarioResponse'
import useLoader from '../../../hooks/useLoader'
import useNavigation from '../../../hooks/useNavigation'
import { downloadHwilAsset, exportSchedule } from './helperFunction'
import { showToastMessage } from '../../../../../../utils/commonFunctions'
import { HwilComponentType } from '../../../../types'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../constants'

const GeneratedAssets: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {

    const { loading } = useLoader()
    const { formData } = useForm()
    const { missionId, scenarioId } = useNavigation()
    const { scenarioResponse } = useScenarioResponse()
    const { scenarioDeploymentType } = useMemo(() => formData.details, [formData.details])
    const hwilConfigGenerated = useMemo(() => formData.assets.hwilAssets.status === 'completed', [formData.assets.hwilAssets.status])
    const hwilComponents = useMemo(() => formData.assets.hwilAssets.data || [], [formData.assets.hwilAssets.data])
    const loader = useMemo(() => loading.execution === 'Connecting', [loading.execution])

    const exportScheduleData = async () => {
        if (!missionId || !scenarioId) return
        try {
            const response = await exportSchedule(missionId, scenarioId)
            showToastMessage('success', response.message);
        } catch (error: any) {
            showToastMessage('error', error.message || 'Failed to export schedule')
        }
    }

    const downloadHwilConfigFile = async (file: HwilComponentType) => {
        if (!missionId || !scenarioId) return
        try {
            await downloadHwilAsset(file, missionId, scenarioId)
        } catch (error: any) {
            showToastMessage('error', error.message || 'Failed to download asset')
        }
    }

    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                <div className={`generated__assets_outer_container`}>
                    <div className='generated__assets_inner_container'>
                        <span className='generated__assets_inner_container__text'>On-Board Schedule</span>
                        <div className='generated__assets_inner__button__container'>
                            <CustomToolTip title='Export Schedule' placement='top'>
                                <button className='generated__assets_inner__button' onClick={exportScheduleData}>
                                    <ExportSvg />
                                </button>
                            </CustomToolTip>
                        </div>
                    </div>
                    {scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP &&
                        <Fragment>
                            <div className='modal__devider' />
                            <div className='information__container'>
                                <InfoSvg />
                                {!hwilConfigGenerated &&
                                    <span className='information__text'>
                                        Generate configuration files to establish successful remote connection. Refer set-up steps in
                                        <a target='_blank' href='https://docs.antaris.space/#id-3.1.2-operation-conops-hybrid-simulation' className='information__link'>
                                            Docs
                                        </a>
                                    </span>
                                }
                                {hwilConfigGenerated && <span className='information__text'>Download & Install Config Files on your remote set-up.</span>}
                            </div>
                            {hwilConfigGenerated && hwilComponents.map(comp => {
                                if (!comp?.fileId) return
                                const connection = hwilComponents && hwilComponents?.find(item => item.uniqueId === comp?.uniqueId)
                                const status_indicator = connection?.status === 'CONNECTED' ? 'connected' : connection?.status === 'TIMEOUT' ? 'timeout' : ''
                                return (
                                    <div className='generated__assets_inner_container' key={comp?.fileId} id={comp?.fileId}>
                                        <span className='generated__assets_inner_container__text'>{comp?.name}</span>
                                        <div className='generated__assets_inner__button__container'>
                                            {scenarioResponse?.status === 'WaitingForConnection' &&
                                                <div className='generated__assets_inner__button cursor-auto'>
                                                    {status_indicator !== '' && <div className={`asset__status__indicator ${status_indicator}`} />}
                                                    {(loader && status_indicator === '') && <div className='spin__loader' />}
                                                </div>
                                            }
                                            <CustomToolTip title='Download File' placement='top'>
                                                <button className='generated__assets_inner__button' onClick={(event) => {
                                                    event.preventDefault()
                                                    downloadHwilConfigFile(comp)
                                                }}>
                                                    <ExportSvg />
                                                </button>
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                )
                            })}
                        </Fragment>
                    }
                </div>
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}


export default GeneratedAssets